import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { QuoteFormModalService } from '../services/quote-form-modal.service';
import { QuoteDetailModalService } from '../services/quote-detail-modal.service';
import { QuoteApiService } from '../services/quote-api.service';
import { QuoteStatusApiService } from '../services/quote-status-api.service';
import { QuoteApprovalApiService } from '../services/quote-approval-api.service';
import { DealApiService } from '../../deals/services/deal-api.service';
import { QuoteResponse, QuoteSearchParams, QuoteMetricsResponse, buildPublicQuoteUrl } from '../models/quote.model';
import { QuoteApprovalDecision, QuoteApprovalPendingItem } from '../models/quote-approval.model';
import { QuoteStatusBadgeComponent } from '../shared/quote-status-badge/quote-status-badge.component';
import { QuoteApprovalDecisionModalComponent } from '../shared/quote-approval-decision-modal/quote-approval-decision-modal.component';
import { QuoteStatusResponse } from '../models/quote-status.model';
import { DealResponse } from '../../deals/models/deal.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

@Component({
  selector: 'app-quotes-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    QuoteStatusBadgeComponent,
    QuoteApprovalDecisionModalComponent,
    HasPermissionDirective
  ],
  templateUrl: './quotes-list.component.html'
})
export class QuotesListComponent implements OnInit, OnDestroy {
  private subs: Subscription[] = [];
  quotes: QuoteResponse[] = [];
  loading = false;
  error = '';
  successMessage = '';

  // Catalogs for filters
  quoteStatuses: QuoteStatusResponse[] = [];
  deals: DealResponse[] = [];

  // Métricas KPI (incidente #41). Requiere Manager+: 403 → ocultar en silencio.
  metrics: QuoteMetricsResponse | null = null;
  metricsLoading = true;
  metricsHidden = false;
  private readonly METRICS_COLLAPSED_KEY = 'quotes-metrics-collapsed';
  metricsCollapsed = localStorage.getItem('quotes-metrics-collapsed') === '1';

  // Bandeja del aprobador (RF-002): aprobaciones pendientes asignadas al usuario actual.
  // Error al cargar → ocultar en silencio (la lista no depende de esto).
  pendingApprovals: QuoteApprovalPendingItem[] = [];
  approvalsExpanded = false;
  showDecisionModal = false;
  decisionApprovalId: number | null = null;
  decisionToMake: QuoteApprovalDecision | null = null;
  decisionQuoteNumber = '';

  // Filters
  showFilters = false;
  filtroQuoteNumber = '';
  filtroStatusId: number | undefined;
  filtroDealId: number | undefined;
  filtroDateFrom = '';
  filtroDateTo = '';
  filtroValidFrom = '';
  filtroValidTo = '';

  // Paginación global
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;

  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'quotes-list-page-size';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 10;

  constructor(
    private quoteApi: QuoteApiService,
    private quoteStatusApi: QuoteStatusApiService,
    private quoteApprovalApi: QuoteApprovalApiService,
    private dealApi: DealApiService,
    private authService: AuthService,
    private router: Router,
    private datetimeService: DatetimeService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private formModal: QuoteFormModalService,
    private detailModal: QuoteDetailModalService
  ) { }

  ngOnInit(): void {
    this.cargarCatalogos();
    this.cargarMetrics();
    this.cargarBandejaAprobaciones();
    this.cargarQuotes();

    // El wizard se monta a nivel de layout y se controla con QuoteFormModalService (permite abrirlo
    // desde la lista o desde el detalle/kanban/lista de negocios). Al guardar refrescamos la lista.
    this.subs.push(this.formModal.saved$.subscribe(() => this.cargarQuotes()));
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  // ---- Bandeja del aprobador (RF-002) ----

  private cargarBandejaAprobaciones(): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.quoteApprovalApi.getPendingApprovals(businessId).subscribe({
      next: (items) => this.pendingApprovals = items,
      // Cualquier error oculta la sección sin ruido: el banner solo aparece si hay pendientes.
      error: () => this.pendingApprovals = []
    });
  }

  toggleAprobaciones(): void {
    this.approvalsExpanded = !this.approvalsExpanded;
  }

  verDetalleAprobacion(item: QuoteApprovalPendingItem): void {
    this.detailModal.open(item.quoteId);
  }

  abrirDecision(item: QuoteApprovalPendingItem, decision: QuoteApprovalDecision): void {
    this.decisionApprovalId = item.id;
    this.decisionToMake = decision;
    this.decisionQuoteNumber = item.quoteNumber;
    this.showDecisionModal = true;
  }

  cerrarDecision(): void {
    this.showDecisionModal = false;
    this.decisionApprovalId = null;
    this.decisionToMake = null;
    this.decisionQuoteNumber = '';
  }

  // El modal ya hizo el POST y el toast: refrescar bandeja y lista (la cotización cambió de estado).
  onDecidido(): void {
    this.cerrarDecision();
    this.cargarBandejaAprobaciones();
    this.cargarQuotes();
  }

  /** Monto de una aprobación pendiente con el código de moneda que trae el item. */
  formatPendingAmount(item: QuoteApprovalPendingItem): string {
    const formatted = new Intl.NumberFormat('es-PE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(item.totalAmount || 0);
    return item.currencyCode ? `${item.currencyCode} ${formatted}` : formatted;
  }

  private cargarCatalogos(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.quoteStatusApi.getAllStatuses().subscribe({
      next: (data) => this.quoteStatuses = data,
      error: () => { }
    });

    this.dealApi.searchDeals({ businessId, page: 0, size: 1000 }).subscribe({
      next: (response) => this.deals = response.content,
      error: () => { }
    });
  }

  // KPIs de cotizaciones. Endpoint restringido a Manager+: cualquier error
  // (incluido 403) oculta la sección sin toast — la lista no depende de esto.
  private cargarMetrics(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.quoteApi.getMetrics(businessId).subscribe({
      next: (metrics) => {
        this.metrics = metrics;
        this.metricsLoading = false;
      },
      error: () => {
        this.metricsHidden = true;
        this.metricsLoading = false;
      }
    });
  }

  toggleMetrics(): void {
    this.metricsCollapsed = !this.metricsCollapsed;
    localStorage.setItem(this.METRICS_COLLAPSED_KEY, this.metricsCollapsed ? '1' : '0');
  }

  /** Monto agregado de métricas en formato es-PE (sin decimales para números grandes). */
  formatMetricAmount(value: number): string {
    return 'S/ ' + new Intl.NumberFormat('es-PE', { maximumFractionDigits: 0 }).format(value || 0);
  }

  cargarQuotes(): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const params: QuoteSearchParams = {
      businessId,
      searchQuoteNumber: this.filtroQuoteNumber || undefined,
      statusId: this.filtroStatusId,
      dealId: this.filtroDealId,
      dateFrom: this.filtroDateFrom || undefined,
      dateTo: this.filtroDateTo || undefined,
      validFrom: this.filtroValidFrom || undefined,
      validTo: this.filtroValidTo || undefined,
      page: this.globalPage,
      size: this.pageSize,
      sortBy: 'datetimeCreated',
      sortDirection: 'DESC'
    };

    this.quoteApi.searchQuotes(params).subscribe({
      next: (response) => {
        this.quotes = response.content;
        this.globalPage = response.page ?? this.globalPage;
        this.globalTotalPages = response.totalPages;
        this.globalTotalElements = response.totalElements;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar cotizaciones';
        this.loading = false;
        console.error('Error:', err);
      }
    });
  }

  buscar(): void {
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarQuotes();
  }

  limpiarFiltro(): void {
    this.filtroQuoteNumber = '';
    this.filtroStatusId = undefined;
    this.filtroDealId = undefined;
    this.filtroDateFrom = '';
    this.filtroDateTo = '';
    this.filtroValidFrom = '';
    this.filtroValidTo = '';
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarQuotes();
  }

  nuevaQuote(): void {
    this.formModal.open();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  // Ver detalle: modal de solo lectura (incidentes #4 y #27 — el estándar del CRM es modal, no
  // navegación). La edición queda en el botón Editar, que abre el wizard por encima del detalle.
  verQuote(quote: QuoteResponse): void {
    this.detailModal.open(quote.id);
  }

  editarQuote(quote: QuoteResponse): void {
    this.formModal.open({ quoteId: quote.id });
  }

  // Abre el editor de la cotización con el modal de envío por correo ya abierto (#28).
  enviarPorCorreo(quote: QuoteResponse): void {
    this.formModal.open({ quoteId: quote.id, sendEmail: true });
  }

  async eliminarQuote(quote: QuoteResponse): Promise<void> {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar cotización',
      message: `¿Está seguro de eliminar la cotización "${quote.quoteNumber}"?`
    });
    if (!ok) return;
    this.quoteApi.deleteQuote(quote.id, businessId).subscribe({
      next: () => {
        this.cargarQuotes();
        this.toast.deleted('Cotización');
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar la cotización' });
      }
    });
  }

  async publicarQuote(quote: QuoteResponse): Promise<void> {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const ok = await this.confirmDialog.confirm({
      title: 'Publicar cotización',
      message: `¿Publicar la cotización "${quote.quoteNumber}"? El estado cambiará a "Publicada" y se generará un enlace público.`,
      confirmText: 'Publicar'
    });
    if (!ok) return;
    this.quoteApi.publishQuote(quote.id, businessId).subscribe({
      next: (updated) => {
        this.successMessage = `Cotización ${updated.quoteNumber} publicada correctamente.`;
        // El enlace público se revela SOLO en esta respuesta: armar la URL de la página pública
        // del front a partir del token y copiarla de inmediato.
        const link = updated.publicToken ? buildPublicQuoteUrl(updated.publicToken) : null;
        if (link) {
          navigator.clipboard?.writeText(link).then(
            () => this.toast.success({ title: 'Enlace copiado', message: 'Enlace de cliente copiado al portapapeles.' }),
            () => this.toast.info({ title: 'Copia manual', message: link })
          );
        }
        this.cargarQuotes();
      },
      error: (err) => {
        this.confirmDialog.alert({ title: 'Error', message: err.error?.message || 'Error al publicar la cotización', variant: 'danger' });
      }
    });
  }

  descargarPdf(quote: QuoteResponse): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.quoteApi.downloadPdf(quote.id, businessId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${quote.quoteNumber}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        this.confirmDialog.alert({ title: 'Error', message: err.error?.message || 'Error al descargar el PDF', variant: 'danger' });
      }
    });
  }

  async cambiarEstado(quote: QuoteResponse): Promise<void> {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const newStatusId = await this.confirmDialog.prompt({
      title: 'Cambiar estado',
      message: 'Ingrese el ID del nuevo estado:'
    });
    if (newStatusId) {
      this.quoteApi.changeStatus(quote.id, businessId, +newStatusId).subscribe({
        next: () => {
          this.cargarQuotes();
        },
        error: (err) => {
          this.confirmDialog.alert({ title: 'Error', message: err.error?.message || 'Error al cambiar el estado', variant: 'danger' });
        }
      });
    }
  }

  async nuevaVersion(quote: QuoteResponse): Promise<void> {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const versionNotes = await this.confirmDialog.prompt({
      title: 'Nueva versión',
      message: `Nueva versión de "${quote.quoteNumber}". Ingrese notas de versión (opcional):`,
      inputType: 'textarea',
      required: false
    });
    if (versionNotes === null) return; // cancelled

    this.quoteApi.createNewVersion(quote.id, businessId, versionNotes || undefined).subscribe({
      next: (newQuote) => {
        this.toast.created('Versión de cotización');
        this.cargarQuotes();
        this.formModal.open({ quoteId: newQuote.id });
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al crear nueva versión' });
      }
    });
  }

  // Duplica como una cotización NUEVA e independiente (número nuevo, Borrador, v1)
  // y navega al editor de la copia.
  duplicarQuote(quote: QuoteResponse): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.quoteApi.duplicateQuote(quote.id, businessId).subscribe({
      next: (copy) => {
        this.toast.created('Cotización');
        this.cargarQuotes();
        this.formModal.open({ quoteId: copy.id });
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo duplicar la cotización' });
      }
    });
  }

  getDealName(dealId: number): string {
    const deal = this.deals.find(d => d.id === dealId);
    return deal?.name || `Deal #${dealId}`;
  }

  /** Resolves the best expiry date field: datetimeExpires (v2) or legacy validUntil. */
  getExpiresDate(quote: QuoteResponse): string | undefined {
    return quote.datetimeExpires || quote.validUntil;
  }

  // Estados terminales: no tiene sentido avisar vencimiento de cotizaciones ya cerradas.
  private static readonly TERMINAL_STATUSES = ['Aceptada', 'Rechazada', 'Expirada', 'Reemplazada'];

  /** Días (enteros, redondeo hacia arriba) hasta el vencimiento. Negativo = ya venció. */
  private getDaysToExpire(quote: QuoteResponse): number | null {
    const expires = this.getExpiresDate(quote);
    if (!expires) return null;
    const time = new Date(expires).getTime();
    if (isNaN(time)) return null;
    return Math.ceil((time - Date.now()) / 86400000);
  }

  /** Aviso ámbar: vence dentro de 7 días (y aún no venció) en cotizaciones activas. */
  isExpiringSoon(quote: QuoteResponse): boolean {
    if (QuotesListComponent.TERMINAL_STATUSES.includes(quote.statusName)) return false;
    const days = this.getDaysToExpire(quote);
    return days !== null && days >= 0 && days <= 7;
  }

  /** Aviso rojo: ya venció pero el estado aún no es Expirada (ni otro estado terminal). */
  isOverdue(quote: QuoteResponse): boolean {
    if (QuotesListComponent.TERMINAL_STATUSES.includes(quote.statusName)) return false;
    const days = this.getDaysToExpire(quote);
    return days !== null && days < 0;
  }

  expiringTooltip(quote: QuoteResponse): string {
    const days = this.getDaysToExpire(quote) ?? 0;
    if (days <= 0) return 'Vence hoy';
    return days === 1 ? 'Vence en 1 día' : `Vence en ${days} días`;
  }

  /** true si hay algún filtro aplicado (para diferenciar los estados vacíos). */
  get hasActiveFilters(): boolean {
    return !!(this.filtroQuoteNumber
      || this.filtroStatusId != null
      || this.filtroDealId != null
      || this.filtroDateFrom || this.filtroDateTo || this.filtroValidFrom || this.filtroValidTo);
  }

  formatDate(date?: string): string {
    return this.datetimeService.formatDateNumeric(date);
  }

  formatCurrency(amount: number, quote?: QuoteResponse): string {
    const symbol = quote?.currencySymbol || 'S/';
    return `${symbol} ${amount.toFixed(2)}`;
  }

  // Paginación
  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.globalTotalPages) return;
    this.globalPage = page;
    this.cargarQuotes();
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    localStorage.setItem(this.PAGE_SIZE_KEY, String(size));
    this.buscar();
  }

  get pagesArray(): number[] { return Array.from({ length: this.globalTotalPages }, (_, i) => i + 1); }
  get hasPrevPage(): boolean { return this.globalPage > 0; }
  get hasNextPage(): boolean { return this.globalPage < this.globalTotalPages - 1; }
  get pageDisplay(): number  { return this.globalPage + 1; }
}
